import React, { useState, useRef, useEffect } from 'react';
import {
  Pencil, LayoutTemplate, Copy, Heart, ChevronUp, ChevronDown, Trash2,
  Plus, Maximize2, Link2, Bookmark, MoreHorizontal,
} from 'lucide-react';
import { blockLabels } from '../content-editor/blockTypes';
import SectionControlCard from './SectionControlCard';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

/**
 * SectionHoverOverlay — Iter 150 redesign.
 *
 * Two modes:
 *   • Hover (no selection)  → slim icon-only toolbar pinned top-right:
 *       Edit · Layouts · Move ▾ · More ▾
 *   • Selected (sticky)    → white SectionControlCard pinned top-right
 *     with the full action menu (Edit, Layouts, Duplicate, Save as
 *     Template, Move Up/Down, Delete).
 *
 * The "Add Block" pill stays top-left regardless of state because adding
 * blocks is the most-used in-section action.
 */
function IconButton({ icon: Icon, onClick, testid, title, disabled }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); if (!disabled) onClick?.(); }}
      disabled={disabled}
      data-testid={testid}
      title={title}
      className={`p-1.5 rounded-full transition-all ${
        disabled ? 'opacity-30 cursor-not-allowed' : 'hover:bg-white/[0.10] cursor-pointer'
      }`}
    >
      <Icon size={12} className="text-white" />
    </button>
  );
}

export default function SectionHoverOverlay({
  block,
  onEdit,
  onViewLayouts,
  onDuplicate,
  onFavorite,
  onMoveUp,
  onMoveDown,
  onDelete,
  onAddBlock,
  onSetHeight,
  onSetAttached,
  isFirst,
  isLast,
  isFavorited,
  isSelected = false,
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [heightMenuOpen, setHeightMenuOpen] = useState(false);
  const moreRef = useRef(null);
  const heightRef = useRef(null);
  const currentHeight = block?.content?.section_height || 'normal';
  const isAttached = !!block?.content?.attached;
  const HEIGHTS = [
    { v: 'compact',   label: 'Compact'    },
    { v: 'normal',    label: 'Normal'     },
    { v: 'tall',      label: 'Tall'       },
    { v: 'fullbleed', label: 'Full Bleed' },
  ];

  // Close popovers on outside click
  useEffect(() => {
    if (!moreOpen && !heightMenuOpen) return;
    const onDoc = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
      if (heightRef.current && !heightRef.current.contains(e.target)) setHeightMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [moreOpen, heightMenuOpen]);

  const handleSaveLayout = async () => {
    const label = (await themedPrompt('Name this saved layout:', blockLabels[block.type] || 'My Section'));
    if (!label) return;
    // Persist to backend using the SAME endpoint the AddSectionModal reads
    // from (`/api/builder/saved_layouts`), so the "My Layouts" tab picks
    // it up immediately.
    try {
      const axios = (await import('axios')).default;
      const { toast } = await import('sonner');
      const API = process.env.REACT_APP_BACKEND_URL + '/api';
      const token = localStorage.getItem('token');
      await axios.post(`${API}/builder/saved_layouts`, {
        label,
        block,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Saved "${label}" — available in Add Section → My Layouts`);
    } catch (e) {
      const { toast } = await import('sonner');
      toast.error(e?.response?.data?.detail || 'Could not save layout');
    }
    // Also fire the legacy event for anyone listening
    window.dispatchEvent(new CustomEvent('wb:save-layout', { detail: { blockId: block.id, label } }));
    setMoreOpen(false);
  };

  return (
    <>
      {/* TOP-LEFT — "+ Add Block" (always available on hover or selection) */}
      <button
        onClick={(e) => { e.stopPropagation(); onAddBlock(); }}
        className={`absolute top-3 left-3 z-30 px-3 py-1.5 bg-[#15152E] border border-white/[0.12] rounded-full flex items-center gap-1.5 text-[11px] font-bold text-white uppercase tracking-wider hover:bg-[#1A1A3E] hover:border-[#8B5CF6]/40 shadow-[0_4px_20px_rgba(0,0,0,0.4)] wb-hover-chip wb-hover-chip-clickable ${
          isSelected ? 'opacity-100 visible pointer-events-auto' : ''
        }`}
        data-testid={`section-add-block-${block.id}`}
      >
        <Plus size={11} /> ADD BLOCK
      </button>

      {/* SELECTED — bigger control card */}
      {isSelected && (
        <SectionControlCard
          block={block}
          onEdit={onEdit}
          onViewLayouts={onViewLayouts}
          onDuplicate={onDuplicate}
          onSaveTemplate={handleSaveLayout}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          onDelete={onDelete}
          isFirst={isFirst}
          isLast={isLast}
        />
      )}

      {/* HOVER (when NOT selected) — slim icon toolbar */}
      {!isSelected && (
        <div
          className="absolute top-3 right-3 z-30 flex items-center gap-0.5 bg-[#15152E] border border-white/[0.12] rounded-full px-1 py-1 shadow-[0_4px_20px_rgba(0,0,0,0.4)] wb-hover-chip wb-hover-chip-clickable"
          data-testid={`section-controls-${block.id}`}
        >
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-white hover:bg-white/[0.08] rounded-full flex items-center gap-1.5 transition-all"
            data-testid={`section-edit-${block.id}`}
            title="Edit section"
          >
            <Pencil size={11} /> Edit
          </button>
          <IconButton icon={LayoutTemplate} onClick={onViewLayouts} testid={`section-layouts-${block.id}`} title="Layouts" />
          <div className="w-px h-4 bg-white/10 mx-0.5" />
          <IconButton icon={ChevronUp}   onClick={onMoveUp}   testid={`section-up-${block.id}`}   title="Move up"   disabled={isFirst} />
          <IconButton icon={ChevronDown} onClick={onMoveDown} testid={`section-down-${block.id}`} title="Move down" disabled={isLast} />
          <div className="w-px h-4 bg-white/10 mx-0.5" />
          {/* More menu */}
          <div className="relative" ref={moreRef}>
            <button
              onClick={(e) => { e.stopPropagation(); setMoreOpen(o => !o); setHeightMenuOpen(false); }}
              className="p-1.5 hover:bg-white/[0.08] rounded-full transition-all"
              data-testid={`section-more-${block.id}`}
              title="More"
            >
              <MoreHorizontal size={12} className="text-white" />
            </button>
            {moreOpen && (
              <div
                className="absolute top-full right-0 mt-2 z-40 min-w-[180px] bg-white rounded-xl border border-gray-200 shadow-[0_12px_40px_-12px_rgba(15,23,42,0.25)] py-1.5"
                onClick={(e) => e.stopPropagation()}
                data-testid={`section-more-menu-${block.id}`}
              >
                <MoreItem icon={Copy}     label="Duplicate"        onClick={() => { onDuplicate(); setMoreOpen(false); }} testid={`section-duplicate-${block.id}`} />
                <MoreItem icon={Bookmark} label="Save as Template" onClick={handleSaveLayout} testid={`section-save-layout-${block.id}`} />
                <MoreItem icon={Heart}    label={isFavorited ? 'Unfavorite' : 'Favorite'} onClick={() => { onFavorite(); setMoreOpen(false); }} testid={`section-favorite-${block.id}`} active={isFavorited} />
                {onSetHeight && (
                  <div ref={heightRef} className="relative">
                    <MoreItem
                      icon={Maximize2}
                      label={`Height: ${currentHeight}`}
                      onClick={() => setHeightMenuOpen(o => !o)}
                      testid={`section-height-toggle-${block.id}`}
                    />
                    {heightMenuOpen && (
                      <div
                        className="absolute left-full top-0 ml-1 z-50 min-w-[130px] bg-white rounded-xl border border-gray-200 shadow-[0_12px_40px_-12px_rgba(15,23,42,0.25)] py-1.5"
                        data-testid={`section-height-menu-${block.id}`}
                      >
                        {HEIGHTS.map(h => (
                          <button
                            key={h.v}
                            type="button"
                            onClick={() => { onSetHeight(block.id, h.v); setHeightMenuOpen(false); setMoreOpen(false); }}
                            className={`w-full text-left px-3 py-1.5 text-[12px] font-medium rounded-md transition-all ${
                              currentHeight === h.v ? 'text-[#5B21B6] bg-[#8B5CF6]/10' : 'text-gray-700 hover:bg-gray-100'
                            }`}
                            data-testid={`section-height-${h.v}-${block.id}`}
                          >
                            {h.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {onSetAttached && !isFirst && (
                  <MoreItem
                    icon={Link2}
                    label={isAttached ? 'Detach from above' : 'Attach to above'}
                    onClick={() => { onSetAttached(block.id, !isAttached); setMoreOpen(false); }}
                    testid={`section-attached-toggle-${block.id}`}
                    active={isAttached}
                  />
                )}
                <div className="my-1 mx-2 h-px bg-gray-200" />
                <MoreItem
                  icon={Trash2}
                  label="Delete"
                  onClick={() => { onDelete(); setMoreOpen(false); }}
                  testid={`section-delete-${block.id}`}
                  danger
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* SECTION LABEL PILL — Iter 181: always-visible, no transition.
          Previously this pill used `opacity-0 group-hover/section:opacity-100`
          + `transition-all`, which fired a 150ms opacity fade-in/out every
          time the cursor crossed the section boundary. As the user moved
          between adjacent sections this caused a "blinking caption"
          flicker. Match the iter 178o always-visible policy used for the
          ADD BLOCK pill + slim toolbar. The pill is small (10-px text),
          unobtrusive, and stable.*/}
      <div
        className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 px-2.5 py-1 bg-[#15152E]/95 backdrop-blur-sm border border-white/[0.1] rounded-full text-[9.5px] uppercase tracking-[0.2em] font-bold text-[#A78BFA] opacity-100 pointer-events-none wb-hover-chip"
      >
        {blockLabels[block.type] || block.type}
      </div>
    </>
  );
}

function MoreItem({ icon: Icon, label, onClick, testid, danger, active }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      data-testid={testid}
      className={`w-full px-3 py-2 flex items-center gap-2.5 text-[12px] font-medium rounded-md transition-all ${
        danger ? 'text-red-600 hover:bg-red-50' :
        active ? 'text-[#5B21B6] hover:bg-[#8B5CF6]/10' :
        'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
      }`}
    >
      <Icon size={13} className="shrink-0" />
      <span className="text-left flex-1">{label}</span>
    </button>
  );
}

/** Blue pill button used between sections to insert a brand-new section */
export function AddSectionPill({ onClick, testid }) {
  return (
    // Zero-height overlay at the section seam — takes NO layout space, so the
    // editor canvas is pixel-identical to the live site (Squarespace parity).
    <div className="relative h-0 z-[35]" data-testid={testid || 'add-section-pill'}>
    <div className="absolute left-1/2 -translate-x-1/2 -top-5 h-10 w-56 flex items-center justify-center group/addsec">
      <button
        onClick={onClick}
        className="relative z-10 px-5 py-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[11px] font-bold uppercase tracking-[0.18em] rounded-full shadow-[0_4px_20px_rgba(59,130,246,0.4)] opacity-0 group-hover/addsec:opacity-100 transition-all flex items-center gap-1.5"
        data-testid="add-section-btn"
      >
        <Plus size={11} /> Add Section
      </button>
    </div>
    </div>
  );
}
